import { useMemo } from 'react';
import { ProductInterface } from '../../../services/exports/Interfaces';
import classnames from 'classnames';
import PriceComparison from '../../menu/PriceComparison';
import { useTranslation } from 'react-i18next';
import useProductLikes from '../../../hooks/global/useProductLikes';
import useProductIdentifiers from '../../../hooks/menu/useProductIdentifiers';
import SeoResource from '../../../services/resources/SeoResource';
import HeartIcon from '../../../assets/logo/HeartIcon';
import CustomLabel from '../../labels/CustomLabel';
import Theme from '../../Theme';
import useMenu from '../../../hooks/menu/useMenu';
import useBasket from '../../../hooks/basket/useBasket';
import ThumbUpIcon from '../../../modules/mobile/assets/icons/ThumbUpIcon';
import useMobileApp from '../../../hooks/mobile-app/useMobileApp';
import PlusIcon from '../../../assets/logo/PlusIcon';
import useTheme from '../../../hooks/ui/useTheme';

interface Props {
  product: ProductInterface;
  discount?: string | number;
  addedQuantity?: number;
  toggleProduct?: () => void;
  className?: string;
}

const MultiSelectProduct = (props: Props): JSX.Element => {
  const { product } = props;
  const { isMobileApp } = useMobileApp();
  const { items } = useBasket();

  const addedQuantity = useMemo(
    () => items.reduce((prev, curr) => (curr.product_id === product.id ? prev + curr.quantity : prev), 0),
    [items],
  );

  const config = {
    ...props,
    addedQuantity,
  };

  return isMobileApp ? <MultiSelectProduct.Mobile {...config} /> : <MultiSelectProduct.Web {...config} />;
};

export default MultiSelectProduct;

MultiSelectProduct.Web = ({ product, toggleProduct, addedQuantity }: Props): JSX.Element => {
  const { productHeight } = useMenu();

  return (
    <article
      className={'w-full flex flex-row select-none relative overflow-hidden border rounded-2xl gap-2 cursor-pointer'}
      style={{ height: productHeight }}
      onClick={() => toggleProduct && toggleProduct()}
    >
      <MultiSelectProduct.LeftColumn product={product} isMobile={false} />
      <MultiSelectProduct.RightColumn product={product} addedQuantity={addedQuantity} />
      <MultiSelectProduct.SoldOutOverlay product={product} />
    </article>
  );
};

MultiSelectProduct.Mobile = ({ product, toggleProduct, addedQuantity }: Props): JSX.Element => {
  const { productHeight } = useMenu();

  return (
    <article
      className={'w-full flex flex-row select-none relative overflow-hidden gap-2 py-md cursor-pointer'}
      style={{ height: productHeight }}
      onClick={() => toggleProduct && toggleProduct()}
    >
      <MultiSelectProduct.LeftColumn product={product} isMobile={true} />
      <MultiSelectProduct.RightColumn product={product} addedQuantity={addedQuantity} />
      <MultiSelectProduct.SoldOutOverlay product={product} />
    </article>
  );
};

MultiSelectProduct.SoldOutOverlay = ({ product }: { product: ProductInterface }): JSX.Element => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });
  const { isMobileApp } = useMobileApp();

  if (product?.is_available) {
    return null;
  }

  return (
    <div className={classnames('top-0 left-0 absolute p h-full w-full', { 'py-md': isMobileApp })}>
      <div className="flex flex-col items-center justify-center h-full rounded-xl bg-black bg-opacity-50">
        <div className="flex flex-col absolute inset-0 items-center justify-center">
          <p className="text-white text-center">{t('labels.sold_out')}</p>
        </div>
      </div>
    </div>
  );
};

MultiSelectProduct.PriceRow = ({ product }: { product: ProductInterface }): JSX.Element => {
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = isLiked(product.id);
  const theme = useTheme();
  const originalPrice = product.base_price;

  return (
    <div className={'flex flex-row items-center w-full relative text-gray-700 font-semibold'}>
      <div className="h-full flex flex-col justify-end">
        <PriceComparison
          actualPrice={product?.actual_price}
          originalPrice={originalPrice}
          actualPriceStyle={{
            fontWeight: 'semibold',
            color: theme.color.black.DEFAULT,
          }}
          originalPriceStyle={{
            fontWeight: 'semibold',
          }}
        />
      </div>
      <div className="flex flex-row items-center">
        <div
          className="ml-xs flex items-center"
          onClick={(e) => {
            e.stopPropagation();
            toggleProductLike(product.id);
          }}
        >
          <HeartIcon
            filled={liked}
            className={classnames('h-3', {
              'fill-danger': liked,
              'fill-gray-700': !liked,
            })}
          />
          <span className="text-xs text-gray-700 ml-2xs w-3">{product.likes_count}</span>
        </div>
      </div>
    </div>
  );
};

MultiSelectProduct.ProductImage = ({
  product,
  addedQuantity,
}: {
  product: ProductInterface;
  addedQuantity: number;
}): JSX.Element => {
  const { isMobileApp } = useMobileApp();

  return (
    <div className="relative h-full">
      <div
        className={classnames('flex h-full shrink-none overflow-hidden relative', {
          'lg:py-none lg:pr-none py-md pr-sm lg:rounded-none': !isMobileApp,
        })}
      >
        <div className={'relative h-full w-full my-auto aspect-square object-cover'}>
          <img
            src={product?.thumbnail_url}
            alt={SeoResource.toAltText(`${product?.name} image`)}
            className={'relative h-full w-full my-auto aspect-square object-cover lg:rounded-none rounded-lg'}
          />
        </div>
      </div>
      <MultiSelectProduct.AddButton addedQuantity={addedQuantity} />
    </div>
  );
};

MultiSelectProduct.AddButton = ({
  addedQuantity,
  className,
}: {
  addedQuantity: number;
  className?: string;
}): JSX.Element => {
  const { isMobileApp } = useMobileApp();
  if (addedQuantity) {
    return (
      <div
        className={classnames(
          'absolute bottom-3 right-3 bg-white rounded-lg lg:w-8 lg:h-8 w-7 h-7 flex items-center justify-center border',
          className,
          {
            '!bottom-0 !right-0': isMobileApp,
          },
        )}
      >
        <h6
          className="text-sm text-gray-700 font-medium"
          style={{
            lineHeight: 1,
          }}
        >
          {addedQuantity}
        </h6>
      </div>
    );
  }

  return (
    <div
      className={classnames(
        'absolute lg:bottom-2 lg:right-2 bottom-4 right-3 bg-white rounded-lg lg:w-8 lg:h-8 w-7 h-7 flex items-center justify-center border border-gray',
        className,
        {
          '!bottom-0 !right-0': isMobileApp,
        },
      )}
    >
      <PlusIcon color={Theme.color.gray[700]} className="!h-[12px] !w-[12px]" />
    </div>
  );
};

MultiSelectProduct.Labels = ({ product }: { product: ProductInterface }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });
  const { isBogoProduct } = useMenu();
  const { emojis } = useProductIdentifiers(product);

  if (isBogoProduct(product.id)) {
    return (
      <CustomLabel
        className="text-xs z-10 w-fit px-lg"
        backgroundColor={Theme.color.green['300']}
        textColor={Theme.color.primary}
        label={t('labels.bogo')}
      />
    );
  }

  return (
    <div className="flex flex-row items-center gap-x-2">
      {emojis.isNotEmpty() && <p className="text-right">{emojis.implode(' ')}</p>}
      <MultiSelectProduct.Bogo product={product} />
    </div>
  );
};

MultiSelectProduct.LeftColumn = ({ product, isMobile }: { product: ProductInterface; isMobile: boolean }) => {
  const { isMobileApp } = useMobileApp();
  return (
    <div
      className={classnames('flex flex-col flex-1 shrink-0 relative justify-between', {
        'py-md px-md': !isMobileApp,
      })}
    >
      <div className={'flex flex-row justify-between flex-1'}>
        <div className={'flex flex-1 flex-col justify-between'}>
          <div className="flex flex-col space-y-[4px]">
            <MultiSelectProduct.BestSeller product={product} />
            <div className={classnames('flex flex-col space-y-xs', { 'space-y-xs': isMobile })}>
              <h6 className={'font-normal text-black line-clamp-1 break-words'}>{product?.name}</h6>
              <MultiSelectProduct.PriceRow product={product} />
              {product?.description && (
                <p className="text-gray-700 font-medium line-clamp-2 break-words text-xs">{product.description}</p>
              )}
            </div>
          </div>
          <MultiSelectProduct.Labels product={product} />
        </div>
      </div>
    </div>
  );
};

MultiSelectProduct.RightColumn = ({ product, addedQuantity }: { product: ProductInterface; addedQuantity: number }) => {
  if (!product?.thumbnail_url) {
    return (
      <div className={classnames('flex h-full w-[50px] overflow-hidden relative')}>
        <MultiSelectProduct.AddButton addedQuantity={addedQuantity} />
      </div>
    );
  }

  return <MultiSelectProduct.ProductImage product={product} addedQuantity={addedQuantity} />;
};

MultiSelectProduct.Discount = ({ discount }: { discount: string | number }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:Product:ProductItem' });
  if (!discount) {
    return null;
  }

  return (
    <div className="w-fit bg-brand-primary/30 px-md py-2xs rounded-[4px] flex items-center justify-center">
      <span className="text-brand-primary text-[10px]">{t('discount_badge', { value: discount })}</span>
    </div>
  );
};

MultiSelectProduct.Bogo = ({ product }: { product: ProductInterface }) => {
  const { isBogoProduct } = useMenu();
  if (!isBogoProduct(product.id)) {
    return null;
  }

  return (
    <div className="w-fit bg-brand-primary/30 px-md py-2xs rounded-[4px] flex items-center justify-center">
      <span className="text-brand-primary text-[10px]">Buy 1, Get 1 Free</span>
    </div>
  );
};

MultiSelectProduct.BestSeller = ({ product }: { product: ProductInterface }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Menu:MultiSelectMenuItems' });
  const theme = useTheme();
  if (!product.popularity_rank) {
    return null;
  }

  return (
    <span className="flex gap-x-1 font-medium text-xs items-center">
      <ThumbUpIcon color={theme.color.brand.primary} />
      {t('labels.most_popular', { rank: product?.popularity_rank })}
    </span>
  );
};
